import { useState } from "react";
import { X, Send } from "lucide-react";
import { addNotificationComment } from "@/lib/api";
import { MentionInput } from "@/components/admin/MentionInput";
import { MentionText } from "@/components/admin/MentionText";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

/**
 * NotificationDetailModal — expanded view of a single inbox item with a Slack-
 * style comments thread. Comments support `@handle` mentions; when saved, the
 * backend emits a `mention` notification to each mentioned teammate.
 */
export function NotificationDetailModal({ notification, onClose, onUpdated }) {
  const [comments, setComments] = useState(notification.comments || []);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await addNotificationComment(notification.id, body);
      setComments(prev => [...prev, r.comment]);
      setBody("");
      toast.success("Comment added");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="notif-detail-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="p-5 bg-ccc-plum text-ccc-ivory flex justify-between items-start">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ccc-gold">
              {notification.kind.replace("_", " ")}
            </div>
            <h3 className="font-serif text-xl mt-1 break-words">{notification.title}</h3>
            {notification.subtitle && (
              <p className="text-sm text-stone-300 mt-1">{notification.subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="notif-detail-close"
            className="text-ccc-ivory hover:text-ccc-gold p-1"
          >
            <X size={20}/>
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-3">
          <h4 className="text-xs uppercase tracking-wider text-stone-500">
            Comments {comments.length > 0 && `(${comments.length})`}
          </h4>
          {comments.length === 0 ? (
            <div className="text-sm text-stone-400 italic" data-testid="notif-detail-no-comments">
              No comments yet. Type <span className="font-mono">@handle</span> to loop in a teammate.
            </div>
          ) : (
            <ul className="space-y-3">
              {comments.map(c => {
                let when = c.created_at;
                try { when = formatDistanceToNow(new Date(c.created_at), { addSuffix: true }); } catch { /* ignore */ }
                return (
                  <li
                    key={c.id}
                    className="border-b border-stone-100 pb-2 last:border-b-0"
                    data-testid={`notif-comment-${c.id}`}
                  >
                    <div className="flex justify-between text-xs text-stone-500">
                      <span className="font-medium text-ccc-plum">{c.author_name || c.author_email}</span>
                      <span>{when}</span>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">
                      <MentionText text={c.body}/>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-4 bg-stone-50 border-t border-stone-200">
          <MentionInput
            value={body}
            onChange={setBody}
            placeholder="Add a comment. Type @ to mention a teammate."
            rows={2}
            className="w-full px-3 py-2 border border-stone-300 text-sm focus:outline-none focus:border-ccc-purple resize-none font-serif"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid="notif-comment-send"
              className="inline-flex items-center gap-1 bg-ccc-plum hover:bg-ccc-purple text-ccc-ivory px-3 py-1.5 text-xs uppercase tracking-wider font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={12}/> {sending ? "Sending…" : "Comment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
